import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScheduleExport } from "../../lib/schedule-document/use-schedule-export";
import { useCustomArtStore } from "../../stores/custom-art-store";
import { exportSchedulePowerPoint, type PowerPointExportOptions } from "../preview-canvas/export-powerpoint";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PowerPointExportDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const hasCustomArt = useCustomArtStore((state) => state.layers.length > 0);
  const [options, setOptions] = useState<PowerPointExportOptions>({
    preserveTextAppearance: false,
    embedBackground: false,
    includeCustomArt: true,
  });
  const fields = [
    {
      key: "preserveTextAppearance" as const,
      label: t("powerpoint.preserveTextAppearance"),
      help: t("powerpoint.preserveTextAppearanceHelp"),
      hidden: false,
    },
    {
      key: "embedBackground" as const,
      label: t("powerpoint.embedBackground"),
      help: t("powerpoint.embedBackgroundHelp"),
      hidden: false,
    },
    {
      key: "includeCustomArt" as const,
      label: t("powerpoint.includeCustomArt"),
      help: t("powerpoint.includeCustomArtHelp"),
      hidden: !hasCustomArt,
    },
  ];

  const { exporting, failed, changeOpen, exportSchedule } = useScheduleExport(
    "pptx",
    async (data, filename, art) => await exportSchedulePowerPoint(data, filename, art, t("art.offline"), options),
    onOpenChange,
  );

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent closeLabel={t("powerpoint.close")} closeDisabled={exporting}>
        <DialogHeader>
          <DialogTitle>{t("powerpoint.title")}</DialogTitle>
          <DialogDescription>{t("powerpoint.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fields
            .filter((field) => !field.hidden)
            .map((field) => {
              const fieldId = `${id}-${field.key}`;
              return (
                <div key={field.key} className="flex items-start gap-2">
                  <Checkbox
                    id={fieldId}
                    aria-describedby={`${fieldId}-help`}
                    className="mt-0.5"
                    disabled={exporting}
                    checked={options[field.key]}
                    onCheckedChange={(checked) =>
                      setOptions((current) => ({ ...current, [field.key]: checked === true }))
                    }
                  />
                  <div className="space-y-0.5">
                    <label htmlFor={fieldId} className="block font-medium">
                      {field.label}
                    </label>
                    <p id={`${fieldId}-help`} className="text-neutral-500">
                      {field.help}
                    </p>
                  </div>
                </div>
              );
            })}
        </div>
        {failed && (
          <p role="alert" className="text-red-600">
            {t("powerpoint.exportError")}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" size="sm" disabled={exporting} onClick={() => changeOpen(false)}>
            {t("schedule.cancel")}
          </Button>
          <Button type="button" size="sm" disabled={exporting} aria-busy={exporting} onClick={exportSchedule}>
            {exporting ? t("nav.saving") : failed ? t("powerpoint.retry") : t("powerpoint.export")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
